import React, { Fragment, Suspense, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, PathRouteProps, Route, Routes } from 'react-router-dom';
import { Provider, useSelector } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import OneSignalReact from 'react-onesignal';
// mui
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LicenseInfo } from '@mui/x-license-pro';

// redux
import createStore from 'redux/store';
import { getAuth } from 'redux/modules/auth/selectors';

import { theme } from 'themes';
import { InAppNotification } from 'components/app-notifications';
import SnackbarContainer from 'components/snackbar';
import FallbackSpin from 'components/shared/atoms/fallback';
import { routes } from 'routes';

import 'lib/fontawesome';
import SocketListener from 'lib/socket/socket-listener';
import { getUserInfo } from 'utils';
import CONFIG from 'config';
import { initFaro } from 'observability/faro';
// import AnimatedRoutes from 'AnimatedRoutes';

initFaro();

export const { store, persistor } = createStore();
const root = createRoot(document.getElementById('root') as Element);
LicenseInfo.setLicenseKey(CONFIG.mui.licenseKey as string);

const initOneSignal = async () => {
  try {
    await OneSignalReact.init({
      appId: CONFIG.api.oneSignalAppId as string,
      allowLocalhostAsSecureOrigin: true,
    });
    console.log('One Signal Enabled', await OneSignalReact.isPushNotificationsEnabled());
    console.log('OneSignal initialized!');
  } catch (error) {
    console.log('One Signal Init Error', error);
  }
};

export type RouteConfig = PathRouteProps & {
  guard?: any;
  component?: any;
};

const App = () => {
  const { userId, authorities } = getUserInfo();
  const { isAuthenticated } = useSelector(getAuth);

  useEffect(() => {
    if (isAuthenticated && userId && authorities) {
      SocketListener.initSocket({ userId, authority: authorities }, store);
      initOneSignal();
    }
  }, [isAuthenticated]);

  return (
    <BrowserRouter>
      <Suspense fallback={<FallbackSpin />}>
        <Routes>
          {routes.map((route: RouteConfig, i: number) => {
            const Guard = route.guard || Fragment;
            const Component = route.component;

            return (
              <Route
                key={i}
                path={route.path}
                element={
                  <Guard>
                    <Component />
                  </Guard>
                }
              />
            );
          })}
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <InAppNotification />
            <App />
            <SnackbarContainer />
          </ThemeProvider>
        </StyledEngineProvider>
      </PersistGate>
    </Provider>
  </React.StrictMode>
);
